import { uiEffectsEnabled } from "../../../graphics/graphics_quality.js";
import { ImgID } from "../../../image_loader/img_id.js";
import { formatShortDuration } from "../../../common/common_funcs.js";
import { audioController } from "../../../audio/audio_controller.js";
import { AudioClips } from "../../../audio/audio_constants.js";
import { ColorScheme } from "../../common_ui_elements/color_scheme.js";
import { RoundedRectangle } from "../../common_ui_elements/rounded_rectangle.js";
import { TextStyleScheme } from "../../common_ui_elements/text_style_scheme.js";
import { UIElement } from "../../common_ui_elements/ui_element.js";
import { UIElementHolder } from "../../common_ui_elements/ui_element_holder.js";
import { UIImage } from "../../common_ui_elements/ui_image.js";
import { MyTextAlign, MyTextVerticalAlign, UITextElement } from "../../common_ui_elements/ui_text_element.js";
import { BasicEventsController } from "../../../touch_events_controller/basic_events_controller.js";
import { ScreenManager, Screens } from "../../../screen_manager/screen_manager.js";
import { SafeAreas } from "../../safe_areas.js";
import { Game } from "../../../game.js";
import { startDomSampledAnimation } from "../../dom_backend/dom_animations.js";
import { Vector2 } from "../../../common/vector2.js";
import { LocalizationManager } from "../../../localization/localization_manager.js";
import { OfferService, getPlatformProductConfig } from "../../../offers/offer_service.js";
import { IapPlugin } from "../../../plugins/iap_plugin.js";
import { StatisticsStorage } from "../../../storage/statistics_storage.js";
import { RewardFlyToHud } from "../../reward_fly/reward_fly_to_hud.js";
import { onePlusRewardFlySources } from "../one_plus_one_offer_screen/one_plus_reward_fly.js";
import type { OnePlusRewardIcon } from "../one_plus_one_offer_screen/one_plus_reward_fly.js";
import { requestChainOfferStep } from "../chain_offer_screen/chain_offer_purchase_service.js";
import { getOnePlusOneState, onePlusOneAnalyticsContext, ONE_PLUS_TWO_TEMPLATE } from "../../../offers/one_plus_one_offer.js";
import type { OnePlusOneState, OnePlusOneStepState } from "../../../offers/one_plus_one_offer.js";
import { onePlusOneRewardRows } from "../one_plus_one_offer_screen/one_plus_one_offer_screen.js";
import type { IOnePlusOneReward } from "../one_plus_one_offer_screen/one_plus_one_offer_screen.js";
import type { OfferDto, OfferProductDto } from "../../../api/dto/offers.dto.js";
import { RewardTooltip } from "../../tooltip/reward_tooltip.js";
import { renderElementAt } from "../../common_ui_elements/render_utils.js";
import { BackdropLayer } from "../../common_ui_elements/backdrop_layer.js";
import { TelegramAPISettings } from "../../../telegram_api_settings.js";

export interface IOnePlusTwoOfferParams
{
  offer: OfferDto | null;
  placement_id: string;
}

const FREE_BUTTON_HUE_ROTATION = 160;

export class OnePlusTwoOfferScreen extends UIElement
{
  private static readonly DESIGN_W = 1080;
  private static readonly DESIGN_H = 1920;

  // The three XD artboards (1080x1920, 1440x1920, 1080x2340) differ only along
  // the height axis: the body keeps its 1080-wide design and is bottom-anchored,
  // so extra height goes into a taller tiger and a bigger title while the column
  // block only slides down a little.
  private static readonly CONTENT_W = 1029;
  private static readonly COLUMN_W = 322;
  private static readonly COLUMN_H = 1147;
  private static readonly COLUMN_COUNT = 3;
  private static readonly COLUMN_TOP_MIN = 683.7;
  private static readonly COLUMN_TOP_MAX = 701.4;

  private static readonly STRIP_INSET_X = 45;
  private static readonly STRIP_OFFSET_Y = 36.1;
  private static readonly STRIP_W = 231;
  private static readonly STRIP_H = 922;

  private static readonly SLOT_ICON = 195;
  private static readonly SLOT_PITCH = 228;
  private static readonly SLOT_COUNT = 4;
  private static readonly LABEL_OFFSET_Y = 151.1;
  private static readonly LABEL_H = 69;
  private static readonly LABEL_PAD = 30;

  private static readonly BTN_ART_W = 332;
  private static readonly BTN_ART_H = 185;
  private static readonly BTN_FACE_W = 290;
  private static readonly BTN_GLOW = 21;
  private static readonly BTN_CAPTION_PAD = 22;
  private static readonly BTN_INSET = 16;
  private static readonly BTN_OFFSET_Y = 968.2;

  private static readonly LOCK_W = 99;
  private static readonly LOCK_H = 108.8;
  private static readonly LOCK_ANCHOR_X = 305.2;
  private static readonly LOCK_ANCHOR_Y = 38.8;

  private static readonly PLUS_SIZE = 100;
  private static readonly PLUS_OFFSET_Y = 479.3;

  private static readonly CLOSE_RIGHT_INSET = 53;

  private static readonly SKY_W = 1440;
  private static readonly SKY_H = 1280;
  private static readonly SKY_BOTTOM = 707;
  /** Share of the photo's height whose top edge is soft (semi-transparent). */
  private static readonly SKY_SOFT_TOP = 0.03;

  private static readonly TIGER_ASPECT = 793 / 893;
  private static readonly TELEGRAM_TIGER_SHIFT = 72;

  private readonly params: IOnePlusTwoOfferParams;
  private readonly body: UIElementHolder;
  private readonly columns: UIElementHolder;
  private readonly body_w: number;
  private readonly body_h: number;
  private readonly body_y: number;

  private offer: OfferDto | null;
  private state: OnePlusOneState | null = null;
  private busy = false;
  private completed = false;
  /** Reward icons per step id, so a claim can fly exactly what the column showed. */
  private readonly reward_icons = new Map<string, OnePlusRewardIcon[]>();
  private price_text: string | null = null;
  private price_requested_for: string | null = null;

  private events_controller: BasicEventsController | null = null;
  private background_gradient: CanvasGradient | null = null;
  private exiting = false;

  private timer_text: UITextElement | null = null;
  private timer_deadline_ms = 0;
  private has_timer = false;
  private timer_last_shown = "";
  private lock_icons = new Map<number, UIImage>();
  private lock_animating_index = -1;
  private lock_anim_start = 0;

  private confetti_layers: UIImage[] = [];
  private confetti_base_positions: Vector2[] = [];
  private confetti_pos = new Vector2( 0, 0 );
  private confetti_started_at = 0;
  private confetti_painted_at = 0;

  // Gradient, sky and confetti live on a backdrop canvas under the screen
  // canvas; the screen canvas holds only the content, transparent elsewhere,
  // and is repainted only when the content changes.
  private sky: UIImage | null = null;
  private backdrop: BackdropLayer | null = null;
  // The first render can come before the first update(), which then moves
  // every element to its render position and repaints it all a frame later.
  private updated_once = false;

  private static readonly LOCK_ANIM_DURATION_MS = 1000;
  private static readonly LOCK_WOBBLE_SPEED = 0.05;
  private static readonly LOCK_WOBBLE_MAX_ANGLE = 0.2;

  private static readonly CONFETTI_LAYER_COUNT = 2;
  private static readonly CONFETTI_CYCLE_MS = 15000;
  private static readonly CONFETTI_FALL_DESIGN = 1200;
  private static readonly CONFETTI_SWAY_DESIGN = 24;
  private static readonly CONFETTI_LAYER_SCALE_STEP = 0.14;
  /** The sheets drift ~30 css px/s, so 60 Hz is visually identical and halves the blits on 120 Hz screens. */
  private static readonly CONFETTI_FRAME_MS = 1000 / 60 - 1;

  private getCardLayout(): { x: number; width: number; center_x: number }
  {
    const center_x = this.body_w / 2;
    const width = LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.CONTENT_W );

    return { x: center_x - width / 2, width, center_x };
  }

  private getColumnTop(): number
  {
    return LayoutScaler.yFromLayouts(
      OnePlusTwoOfferScreen.COLUMN_TOP_MIN,
      OnePlusTwoOfferScreen.COLUMN_TOP_MAX
    );
  }

  private getColumnGap( card_width: number, column_w: number ): number
  {
    return ( card_width - column_w * OnePlusTwoOfferScreen.COLUMN_COUNT )
      / ( OnePlusTwoOfferScreen.COLUMN_COUNT - 1 );
  }

  public constructor(
    ctx: CanvasRenderingContext2D,
    x: number,
    y: number,
    width: number,
    height: number,
    params: IOnePlusTwoOfferParams
  )
  {
    super( ctx, x, y, width, height, false, false );

    this.params = params;
    this.offer = params.offer;
    this.state = getOnePlusOneState( this.offer );
    this.primePriceFromCache();

    this.body_w = LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.DESIGN_W );
    this.body_h = LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.DESIGN_H );
    this.body_y = height - this.body_h;

    this.body = new UIElementHolder(
      ctx,
      ( width - this.body_w ) / 2,
      this.body_y,
      this.body_w,
      this.body_h
    );
    this.columns = new UIElementHolder( ctx, 0, 0, this.body_w, this.body_h );

    this.initializeEventsController( ctx.canvas );

    const remaining_ms = this.offer ? this.remainingOfferMs() : null;
    this.has_timer = remaining_ms !== null && remaining_ms > 0;
    if ( this.has_timer )
      this.timer_deadline_ms = performance.now() + ( remaining_ms ?? 0 );

    this.createElements();

    if ( this.offer )
      void OfferService.trackShown( this.offer.id, this.analyticsContext() );
  }

  private analyticsContext( chain_step_id?: string ): Record<string, unknown>
  {
    return onePlusOneAnalyticsContext( this.params.placement_id, chain_step_id );
  }

  private titleKey(): string
  {
    return this.offer?.texts?.title_key ?? "OFFER.ONE_PLUS_TWO.TITLE";
  }

  private subtitleKey(): string
  {
    return this.offer?.texts?.description_key ?? "OFFER.ONE_PLUS_TWO.SUBTITLE";
  }

  private remainingOfferMs(): number | null
  {
    if ( !this.offer ) return null;
    const deadline_ms = OfferService.getOfferDisplayEndMs( this.offer );
    if ( deadline_ms === null ) return null;
    const server_now_ms = OfferService.getServerNowMs() ?? Date.now();
    return deadline_ms - server_now_ms;
  }

  private createElements(): void
  {
    this.createSky();
    this.addChild( this.body );

    this.createArt();
    this.createTitle();
    this.createSubtitle();
    this.createCloseIcon();
    this.createTimer();
    this.body.addChild( this.columns );
    this.buildColumns();
  }

  private createSky(): void
  {
    const screen_left = -Game.safeAreaLeft();
    const screen_top = -Game.safeAreaTop();

    // The photo carries the purple fade on its bottom edge, so it is anchored
    // there and may only grow upwards. The artboard keeps its soft top edge
    // off screen; past ~1080x2500 the bottom-anchored body drops far enough to
    // expose it (and the orange top of the background gradient behind it), so
    // the photo is scaled up until its solid part still reaches the top edge.
    const sky_bottom = this.body_y + LayoutScaler.yFromDesign( OnePlusTwoOfferScreen.SKY_BOTTOM );
    const cover_h = ( sky_bottom - screen_top ) / ( 1 - OnePlusTwoOfferScreen.SKY_SOFT_TOP );

    const sky_w = Math.max(
      LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.SKY_W ),
      Game.screenW(),
      cover_h * OnePlusTwoOfferScreen.SKY_W / OnePlusTwoOfferScreen.SKY_H
    );
    const sky_h = Math.round( sky_w * OnePlusTwoOfferScreen.SKY_H / OnePlusTwoOfferScreen.SKY_W );
    const sky_x = Math.max(
      screen_left + Game.screenW() - sky_w,
      Math.min( ( this.body_w - sky_w ) / 2, screen_left )
    );

    const sky = new UIImage(
      this.ctx,
      sky_x,
      sky_bottom - sky_h,
      sky_w,
      sky_h,
      ImgID.UI_OnePlusTwoOfferSky
    );
    // Painted on the backdrop, so kept out of the children (parent for placement only).
    if ( this.dom_mode )
      this.addChild( sky );
    else
      sky.setParentElement( this );
    this.sky = sky;
  }

  private createArt(): void
  {
    this.createConfetti();

    const tiger_h = LayoutScaler.heightFromLayouts( 352, 848 );
    const tiger_w = tiger_h * OnePlusTwoOfferScreen.TIGER_ASPECT;
    const tiger_x = this.getCardLayout().center_x - tiger_w / 2;
    const telegram_shift = TelegramAPISettings.isTelegramEnvironment()
      ? LayoutScaler.yFromDesign( OnePlusTwoOfferScreen.TELEGRAM_TIGER_SHIFT )
      : 0;

    const tiger = new UIImage(
      this.ctx,
      tiger_x,
      LayoutScaler.yFromLayouts( 16, -358 ) + telegram_shift,
      tiger_w,
      tiger_h,
      ImgID.UI_OnePlusTwoOfferTiger
    );
    this.body.addChild( tiger );
  }

  private createConfetti(): void
  {
    const sheet_w = LayoutScaler.widthFromDesign( 1247 );
    const sheet_h = LayoutScaler.heightFromDesign( 1704 );
    const sheet_x = LayoutScaler.xFromDesign( -35.8 );
    const sheet_y = LayoutScaler.yFromLayouts( -360, -566 );

    this.confetti_started_at = performance.now();

    for ( let layer = 0; layer < OnePlusTwoOfferScreen.CONFETTI_LAYER_COUNT; ++layer )
    {
      const scale = 1 + layer * OnePlusTwoOfferScreen.CONFETTI_LAYER_SCALE_STEP;
      const width = sheet_w * scale;
      const height = sheet_h * scale;
      const base = new Vector2( sheet_x - ( width - sheet_w ) / 2, sheet_y - ( height - sheet_h ) / 2 );

      const sheet = new UIImage( this.ctx, base.x, base.y, width, height, ImgID.UI_OnePlusOneOfferConfetti );
      sheet.setFlipX( layer % 2 === 1 );

      // Parented for positioning only: kept out of the children so its
      // per-frame motion does not mark the whole tree dirty.
      if ( this.dom_mode )
        this.body.addChild( sheet );
      else
        sheet.setParentElement( this.body );
      this.confetti_layers.push( sheet );
      this.confetti_base_positions.push( base );
    }

    this.layoutConfetti( this.confetti_started_at );
    this.startConfettiDomLoop();
  }

  private confetti_anims: Animation[] = [];
  private lock_anim: Animation | null = null;

  private startConfettiDomLoop(): void
  {
    if ( !this.dom_mode )
      return;

    if ( !uiEffectsEnabled() )
      return;

    const cycle = OnePlusTwoOfferScreen.CONFETTI_CYCLE_MS;
    const fall = LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.CONFETTI_FALL_DESIGN );
    const sway = LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.CONFETTI_SWAY_DESIGN );
    const count = this.confetti_layers.length;

    for ( let layer = 0; layer < count; ++layer )
    {
      const base = this.confetti_base_positions[ layer ];
      const anim = startDomSampledAnimation( this.confetti_layers[ layer ].getDomNode(), cycle, t =>
      {
        const phase = ( t + layer / count ) % 1;
        return {
          pos: new Vector2( base.x + Math.sin( phase * Math.PI * 2 ) * sway, base.y + ( phase - 0.5 ) * fall ),
          opacity: OnePlusTwoOfferScreen.confettiOpacity( phase ),
        };
      }, { iterations: Infinity, samples: 120 } );
      if ( anim )
        this.confetti_anims.push( anim );
    }
  }

  private cancelDomAnims(): void
  {
    for ( const anim of this.confetti_anims )
      anim.cancel();
    this.confetti_anims = [];
    this.lock_anim?.cancel();
    this.lock_anim = null;
  }

  private layoutConfetti( now: number ): void
  {
    const cycle = OnePlusTwoOfferScreen.CONFETTI_CYCLE_MS;
    const fall = LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.CONFETTI_FALL_DESIGN );
    const sway = LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.CONFETTI_SWAY_DESIGN );
    const elapsed = now - this.confetti_started_at;
    const count = this.confetti_layers.length;

    for ( let layer = 0; layer < count; ++layer )
    {
      const phase = ( elapsed / cycle + layer / count ) % 1;
      const base = this.confetti_base_positions[ layer ];
      this.confetti_pos.set(
        base.x + Math.sin( phase * Math.PI * 2 ) * sway,
        base.y + ( phase - 0.5 ) * fall
      );

      this.confetti_layers[ layer ].setPixelPos( this.confetti_pos, true );
      this.confetti_layers[ layer ].setOpacityImmediate( OnePlusTwoOfferScreen.confettiOpacity( phase ) );
    }
  }

  private updateConfetti( now: number ): void
  {
    if ( this.confetti_layers.length === 0 || this.dom_mode )
      return;

    this.layoutConfetti( now );
  }

  private static confettiOpacity( phase: number ): number
  {
    return OnePlusTwoOfferScreen.smoothStep( 1 - Math.abs( 2 * phase - 1 ) );
  }

  private static smoothStep( t: number ): number
  {
    const clamped = Math.max( 0, Math.min( 1, t ) );

    return clamped * clamped * ( 3 - 2 * clamped );
  }

  private createTitle(): void
  {
    this.addTitleLayer( 358.7, 342.2, 42, "#6C1F04" );
    this.addTitleLayer( 356.7, 339.2, 38, "#F9AF26" );
    this.addTitleLayer( 356.7, 339.2, 28, "#03238B" );
    this.addTitleLayer( 354.7, 336.2, 22, "#5B00FB" );
    this.addTitleLayer( 354.7, 336.2, 6, "#AA6600" );
    this.addTitleLayer( 354.7, 336.2, 0, "#FCEA3E" );
  }

  private addTitleLayer( center_min: number, center_max: number, outline_width: number, color: string ): void
  {
    const card = this.getCardLayout();
    const height = LayoutScaler.heightFromDesign( 183 );
    const is_fill = outline_width === 0;

    const layer = new UITextElement(
      this.ctx,
      card.x,
      LayoutScaler.yFromLayouts( center_min, center_max ) - height / 2,
      card.width,
      height,
      {
        localization_key: this.titleKey(),
        font_size: LayoutScaler.heightFromLayouts( 89, 130 ),
        text_style: TextStyleScheme.geologica,
        text_color: is_fill ? color : ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        outline_width: is_fill ? undefined : LayoutScaler.textBorderSizeFromDesign( outline_width ),
        outline_color: is_fill ? undefined : color,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 60 ),
      }
    );
    this.body.addChild( layer );
  }

  private createSubtitle(): void
  {
    const card = this.getCardLayout();
    const height = LayoutScaler.heightFromDesign( 124 );

    const subtitle = new UITextElement(
      this.ctx,
      card.x,
      LayoutScaler.yFromDesign( 487.3 ) - height / 2,
      card.width,
      height,
      {
        localization_key: this.subtitleKey(),
        font_size: LayoutScaler.textSizeFromDesign( 55 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 800,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_color: "#021A67",
        line_height: 1.09,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 34 ),
      }
    );
    this.body.addChild( subtitle );
  }

  private createTimer(): void
  {
    if ( !this.has_timer )
      return;

    const timer_y = LayoutScaler.yFromDesign( 580.1 );
    const timer_h = LayoutScaler.heightFromDesign( 77.9 );

    const substrate = new UIImage(
      this.ctx,
      LayoutScaler.xFromDesign( 396 ),
      timer_y,
      LayoutScaler.widthFromDesign( 287.9 ),
      timer_h,
      ImgID.UI_OnePlusOneOfferTimerBg
    );
    this.body.addChild( substrate );

    this.timer_text = new UITextElement(
      this.ctx,
      LayoutScaler.xFromDesign( 466 ),
      timer_y,
      LayoutScaler.widthFromDesign( 204 ),
      timer_h,
      {
        text: "",
        font_size: LayoutScaler.textSizeFromDesign( 45 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        center_on_cap_height: true,
        ...TextStyleScheme.timerShadow(),
        font_weight: 900,
      }
    );
    this.body.addChild( this.timer_text );

    this.updateTimer( performance.now() );
  }

  private buildColumns(): void
  {
    this.columns.removeAllChildren();
    this.lock_icons.clear();
    this.reward_icons.clear();
    this.lock_animating_index = -1;

    const steps = this.state?.steps ?? [];
    if ( !steps.length )
      return;

    const card = this.getCardLayout();
    const count = Math.max( 1, steps.length );
    const column_w = count === OnePlusTwoOfferScreen.COLUMN_COUNT
      ? LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.COLUMN_W )
      : ( card.width - this.getColumnGap( card.width, LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.COLUMN_W ) ) * ( count - 1 ) ) / count;
    const gap = this.getColumnGap( card.width, LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.COLUMN_W ) );

    for ( let index = 0; index < count; ++index )
    {
      const column_x = card.x + index * ( column_w + gap );
      this.createColumn( column_x, column_w, steps[ index ] );
      if ( index > 0 )
        this.createPlus( column_x - gap / 2 );
    }

    this.invalidateSubtree();
    this.forceMakeShouldBeRendered();
  }

  private createColumn( column_x: number, column_w: number, state: OnePlusOneStepState ): void
  {
    const panel_y = this.getColumnTop();

    const panel = new RoundedRectangle(
      this.ctx,
      column_x,
      panel_y,
      column_w,
      LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.COLUMN_H ),
      LayoutScaler.cornerRadiusFromDesign( 54 ),
      {
        type: "linear-gradient",
        direction: "vertical",
        stops: [
          { offset: 0, color: "rgba(103, 23, 146, 0.322)" },
          { offset: 1, color: "#65D6FE" },
        ],
      }
    );
    this.columns.addChild( panel );

    const strip_y = panel_y + LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.STRIP_OFFSET_Y );
    const strip_inset = LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.STRIP_INSET_X );

    const strip = new RoundedRectangle(
      this.ctx,
      column_x + strip_inset,
      strip_y,
      column_w - strip_inset * 2,
      LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.STRIP_H ),
      LayoutScaler.cornerRadiusFromDesign( 34 ),
      "#56148F",
      undefined,
      false,
      undefined,
      0,
      {
        color: "rgba(255, 255, 255, 0.678)",
        blur: LayoutScaler.widthFromDesign( 6 ),
        offsetY: LayoutScaler.yFromDesign( 3 ),
      }
    );
    this.columns.addChild( strip );

    const rewards = onePlusOneRewardRows( state.step.rewards as unknown as Record<string, unknown> );
    const slot_count = Math.min( rewards.length, OnePlusTwoOfferScreen.SLOT_COUNT );
    const slot_pitch = LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.SLOT_PITCH );

    const icons: OnePlusRewardIcon[] = [];
    for ( let slot = 0; slot < slot_count; ++slot )
      icons.push( { reward: rewards[ slot ], icon: this.createReward( column_x, column_w, strip_y + slot * slot_pitch, rewards[ slot ], state.status === 'claimed' ) } );
    this.reward_icons.set( state.step.id, icons );

    this.createButton( column_x, column_w, panel_y, state );
  }

  private createReward(
    column_x: number,
    column_w: number,
    slot_y: number,
    reward: IOnePlusOneReward,
    claimed: boolean
  ): UIImage
  {
    const icon_size = OnePlusTwoOfferScreen.SLOT_ICON;
    const label_pad = OnePlusTwoOfferScreen.LABEL_PAD;
    const icon_w = LayoutScaler.widthFromDesign( icon_size );
    const icon_x = column_x + ( column_w - icon_w ) / 2;

    const icon = new UIImage(
      this.ctx,
      icon_x,
      slot_y,
      icon_w,
      LayoutScaler.heightFromDesign( icon_size ),
      reward.icon
    );
    if ( claimed )
      icon.setDimmed( true );
    this.columns.addChild( icon );

    const label = new UITextElement(
      this.ctx,
      icon_x - LayoutScaler.widthFromDesign( label_pad ),
      slot_y + LayoutScaler.yFromDesign( OnePlusTwoOfferScreen.LABEL_OFFSET_Y ),
      LayoutScaler.widthFromDesign( icon_size + label_pad * 2 ),
      LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.LABEL_H ),
      {
        text: reward.label,
        font_size: LayoutScaler.textSizeFromDesign( 59 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        font_weight: 900,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 9 ),
        outline_color: "#003F87",
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 32 ),
      }
    );
    this.columns.addChild( label );
    RewardTooltip.attach( icon, reward.type, [ label ] );
    return icon;
  }

  private createButton( column_x: number, column_w: number, panel_y: number, state: OnePlusOneStepState ): void
  {
    const art_w = OnePlusTwoOfferScreen.BTN_ART_W;
    const art_h = OnePlusTwoOfferScreen.BTN_ART_H;
    const glow = OnePlusTwoOfferScreen.BTN_GLOW;

    const inset = LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.BTN_INSET );
    const btn_w = ( column_w - inset * 2 ) * art_w / OnePlusTwoOfferScreen.BTN_FACE_W;
    const btn_h = LayoutScaler.heightFromDesign( art_h );
    const btn_x = column_x + inset - btn_w * glow / art_w;
    const btn_y = panel_y + LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.BTN_OFFSET_Y );

    const button = new UIImage(
      this.ctx,
      btn_x,
      btn_y,
      btn_w,
      btn_h,
      ImgID.UI_OnePlusOneOfferGreenBtn,
      {},
      true
    );
    // A locked reward keeps the normal green button and coloured lock.
    if ( state.status === 'claimed' )
      button.setDimmed( true );
    else if ( state.status === 'claimable' )
      button.setHueRotation( FREE_BUTTON_HUE_ROTATION );
    button.setOnClick( () => { void this.handleStepClick( state ); } );
    this.columns.addChild( button );

    // The art carries a transparent glow on both sides; fit the caption to the
    // visible face minus a margin, or long captions spill past the button edge.
    const caption_pad = OnePlusTwoOfferScreen.BTN_CAPTION_PAD;
    const caption = new UITextElement(
      this.ctx,
      btn_w * ( glow + caption_pad ) / art_w,
      0,
      btn_w * ( OnePlusTwoOfferScreen.BTN_FACE_W - caption_pad * 2 ) / art_w,
      btn_h,
      {
        text: this.getButtonCaption( state ),
        font_size: LayoutScaler.textSizeFromDesign( state.status === 'paid' ? 50 : 60 ),
        text_style: TextStyleScheme.geologica,
        text_color: ColorScheme.white,
        text_align: MyTextAlign.Center,
        vertical_align: MyTextVerticalAlign.Center,
        ...TextStyleScheme.buttonShadow(),
        outline_color: state.status === 'claimed' ? ColorScheme.non_active_btn_outline
          : state.status === 'claimable' ? ColorScheme.more_lives_purple_outline : ColorScheme.more_lives_buy_outline,
        outline_width: LayoutScaler.textBorderSizeFromDesign( 8 ),
        outline_offset_y: LayoutScaler.yFromDesign( 2 ),
        font_weight: 800,
        line_height: 1.04,
        auto_wrap: false,
        scale_to_width: true,
        min_font_size: LayoutScaler.textSizeFromDesign( 24 ),
      }
    );
    button.addChild( caption, true );

    if ( state.status !== 'locked' )
      return;

    const lock_w = LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.LOCK_W );
    const lock_h = LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.LOCK_H );
    const lock = new UIImage(
      this.ctx,
      btn_w * OnePlusTwoOfferScreen.LOCK_ANCHOR_X / art_w - lock_w / 2,
      btn_h * OnePlusTwoOfferScreen.LOCK_ANCHOR_Y / art_h - lock_h / 2,
      lock_w,
      lock_h,
      ImgID.UI_OnePlusOneOfferLock
    );
    lock.enableRotation();
    button.addChild( lock, true );
    this.lock_icons.set( state.index, lock );
  }

  private getButtonCaption( state: OnePlusOneStepState ): string
  {
    switch ( state.status )
    {
      case 'paid':
        return this.price_text ?? '…';
      case 'claimed':
        return LocalizationManager.t( "OFFER.ONE_PLUS_ONE.CLAIMED" );
      default:
        return LocalizationManager.t( this.offer?.texts?.button_key ?? "OFFER.ONE_PLUS_ONE.FREE" );
    }
  }

  /**
   * Uses a price fetched earlier this session (prepareScreenImages asks the
   * store before the screen opens), so the paid button is built with it
   * instead of '…' and the columns are not rebuilt a few frames in.
   */
  private primePriceFromCache(): void
  {
    const paid = this.state?.steps.find( item => item.status === 'paid' );
    const product = paid?.step.products ? getPlatformProductConfig( paid.step.products ) : null;
    const price = product ? IapPlugin.peekProductDetails( product.productId )?.formatted_price : undefined;
    if ( !this.offer || !paid || !product || !price ) return;

    this.price_text = price;
    this.price_requested_for = paid.step.id;
    void OfferService.trackPriceLoaded( this.offer.id, { ...this.analyticsContext( paid.step.id ), productid: product.productId } );
  }

  private loadPrice( state: OnePlusOneStepState ): void
  {
    if ( !this.offer || this.price_requested_for === state.step.id ) return;
    const product = state.step.products ? this.platformProduct( state.step.products ) : null;
    if ( !product ) return;
    this.price_requested_for = state.step.id;
    const offer_id = this.offer.id;
    const context = { ...this.analyticsContext( state.step.id ), productid: product.productId };
    void ( async () =>
    {
      try
      {
        const details = await IapPlugin.getProductDetails( product.productId );
        this.price_text = details?.formatted_price ?? 'Buy';
        await OfferService.trackPriceLoaded( offer_id, context );
      }
      catch
      {
        this.price_text = 'Buy';
        await OfferService.trackPriceUnavailable( offer_id, context );
      }
      if ( !this.exiting ) this.buildColumns();
    } )();
  }

  private platformProduct( products: NonNullable<OnePlusOneStepState['step']['products']> ): OfferProductDto | null
  {
    return getPlatformProductConfig( products );
  }

  private async handleStepClick( state: OnePlusOneStepState ): Promise<void>
  {
    if ( this.exiting || !this.offer ) return;
    if ( state.status === 'locked' )
    {
      this.wobbleLock( state.index );
      return;
    }
    if ( state.status === 'claimed' || this.busy ) return;

    void audioController.playSound( AudioClips.UI_CLICK );
    this.busy = true;
    const stats_before = StatisticsStorage.load();
    const result = await requestChainOfferStep(
      this.offer.id,
      state.step.id,
      state.status === 'paid' ? 'paid' : 'free',
      { placement_id: this.params.placement_id, placement: this.params.placement_id, template: ONE_PLUS_TWO_TEMPLATE },
    );
    this.busy = false;
    if ( this.exiting ) return;

    const fresh = OfferService.getOffer( this.offer.id );
    const fresh_state = getOnePlusOneState( fresh );
    const fly_sources = result === 'claimed' ? onePlusRewardFlySources( this.reward_icons.get( state.step.id ) ?? [] ) : [];
    if ( result === 'claimed' && ( !fresh_state || fresh_state.all_claimed ) )
    {
      // Everything is taken: let the rewards land in the HUD, then leave.
      this.completed = true;
      this.busy = true;
      const fly_end = RewardFlyToHud.play( this.ctx, this, fly_sources, stats_before );
      window.setTimeout( () => this.exitToMenu( "completed" ), Math.max( 0, fly_end - performance.now() ) );
      return;
    }
    if ( !fresh || !fresh_state )
    {
      this.exitToMenu( "offer_gone" );
      return;
    }
    this.offer = fresh;
    this.state = fresh_state;
    this.buildColumns();
    if ( fly_sources.length )
      RewardFlyToHud.play( this.ctx, this, fly_sources, stats_before );
  }

  private wobbleLock( index: number ): void
  {
    if ( this.lock_animating_index !== -1 || !this.lock_icons.has( index ) )
      return;

    void audioController.playSound( AudioClips.UI_CLICK );

    this.lock_anim_start = performance.now();
    this.lock_animating_index = index;
    const icon = this.lock_icons.get( index );
    icon?.setRotationImmediate( 0 );
    if ( icon && this.dom_mode )
    {
      const duration = OnePlusTwoOfferScreen.LOCK_ANIM_DURATION_MS;
      this.lock_anim?.cancel();
      this.lock_anim = startDomSampledAnimation( icon.getDomNode(), duration, t =>
      {
        const elapsed = t * duration;
        const envelope = Math.sin( t * Math.PI );
        return { rotation: Math.sin( elapsed * OnePlusTwoOfferScreen.LOCK_WOBBLE_SPEED ) * OnePlusTwoOfferScreen.LOCK_WOBBLE_MAX_ANGLE * envelope };
      }, { samples: 60 } );
    }
    this.forceMakeShouldBeRendered();
  }

  private updateLockAnimation( now: number ): void
  {
    const lock = this.lock_animating_index === -1 ? null : this.lock_icons.get( this.lock_animating_index ) ?? null;
    if ( lock === null )
      return;

    const elapsed = now - this.lock_anim_start;
    const duration = OnePlusTwoOfferScreen.LOCK_ANIM_DURATION_MS;

    if ( elapsed >= duration )
    {
      this.lock_animating_index = -1;
      lock.setRotationImmediate( 0 );
      this.forceMakeShouldBeRendered();
      return;
    }

    if ( this.dom_mode )
      return;

    const envelope = Math.sin( elapsed / duration * Math.PI );
    const angle = Math.sin( elapsed * OnePlusTwoOfferScreen.LOCK_WOBBLE_SPEED )
      * OnePlusTwoOfferScreen.LOCK_WOBBLE_MAX_ANGLE * envelope;
    lock.setRotationImmediate( angle );
    this.forceMakeShouldBeRendered();
  }

  private createPlus( gap_center: number ): void
  {
    const plus_size = LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.PLUS_SIZE );
    const plus_y = this.getColumnTop()
      + LayoutScaler.heightFromDesign( OnePlusTwoOfferScreen.PLUS_OFFSET_Y )
      - plus_size / 2;

    const plus = new UIImage(
      this.ctx,
      gap_center - plus_size / 2,
      plus_y,
      plus_size,
      plus_size,
      ImgID.UI_OnePlusOneOfferPlus
    );
    this.columns.addChild( plus );
  }

  private createCloseIcon(): void
  {
    const icon_w = LayoutScaler.widthFromDesign( 110 );
    const icon_h = LayoutScaler.heightFromDesign( 110 );
    const icon = new UIImage(
      this.ctx,
      this.rect.getW() - icon_w - LayoutScaler.widthFromDesign( OnePlusTwoOfferScreen.CLOSE_RIGHT_INSET ),
      this.body_y + LayoutScaler.yFromDesign( 153 ),
      icon_w,
      icon_h,
      ImgID.UI_SettingsDialogCloseBtn,
      {},
      true
    );

    icon.setOnClick( () => this.exitToMenu( "user_close" ) );

    this.addChild( icon );
  }

  private initializeEventsController( canvas: HTMLCanvasElement ): void
  {
    if ( this.events_controller )
      this.events_controller.cleanup();

    this.events_controller = new BasicEventsController( canvas );
    this.events_controller.init(
      ( p: Vector2 ) => { super.onTouchStart( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchEnd( p, performance.now() ); },
      ( p: Vector2 ) => { super.onTouchMove( p, performance.now() ); },
      true
    );
  }

  private updateTimer( now: number ): void
  {
    if ( !this.has_timer || this.timer_text === null )
      return;

    const remaining_ms = this.timer_deadline_ms - now;
    // Once bought, the offer stays until the player takes every free step.
    if ( remaining_ms <= 0 && !this.exiting && this.state?.awaiting_purchase !== false )
    {
      this.exitToMenu( "expired" );
      return;
    }

    const text = formatShortDuration( Math.max( 0, remaining_ms ) );

    if ( text === this.timer_last_shown )
      return;

    this.timer_last_shown = text;
    this.timer_text.setText( text );
    this.invalidateSubtree();
  }

  private exitToMenu( reason: string ): void
  {
    if ( this.exiting )
      return;

    this.exiting = true;

    if ( this.offer )
    {
      void OfferService.trackClosed( this.offer.id, { ...this.analyticsContext(), reason } );
    }

    void ScreenManager.showScreen( Screens.MENU );
  }

  public destroy(): void
  {
    this.cancelDomAnims();
    this.exiting = true;
    this.lock_animating_index = -1;
    for ( const lock of this.lock_icons.values() )
      lock.setRotationImmediate( 0 );
    this.lock_icons.clear();
    this.confetti_layers = [];
    this.confetti_base_positions = [];
    this.backdrop?.destroy();
    this.backdrop = null;

    if ( this.events_controller )
    {
      this.events_controller.cleanup();
      this.events_controller = null;
    }
  }

  public update( now: number ): void
  {
    this.updated_once = true;
    this.updateBasic( now );
    if ( !this.offer && !this.exiting )
    {
      this.exitToMenu( "offer_gone" );
      return;
    }
    const paid = this.state?.steps.find( item => item.status === 'paid' );
    if ( paid && this.price_text === null )
      this.loadPrice( paid );
    this.updateTimer( now );
    this.updateLockAnimation( now );
    this.updateConfetti( now );
  }

  private getBackgroundGradient(): CanvasGradient
  {
    if ( !this.background_gradient )
    {
      // The artboard gradient always spans the tallest design (2340) and is
      // pinned to the bottom, so a 1920-tall screen only shows its lower part.
      const bottom = Game.screenH();
      const gradient = this.ctx.createLinearGradient(
        0,
        bottom,
        0,
        bottom - LayoutScaler.heightFromDesign( 2340 )
      );
      gradient.addColorStop( 0, "#4E5BD4" );
      gradient.addColorStop( 0.506, "#650486" );
      gradient.addColorStop( 1, "#C66026" );
      this.background_gradient = gradient;
    }

    return this.background_gradient;
  }

  public render(): void
  {
    const now = performance.now();
    if ( !this.updated_once ) this.update( now );
    if ( !this.backdrop && !this.exiting && !this.dom_mode )
    {
      this.backdrop = BackdropLayer.under( this.ctx.canvas );
      this.confetti_painted_at = 0;
    }

    if ( this.backdrop && now - this.confetti_painted_at >= OnePlusTwoOfferScreen.CONFETTI_FRAME_MS )
    {
      this.confetti_painted_at = now;
      const back = this.backdrop.ctx;
      back.fillStyle = this.getBackgroundGradient();
      back.fillRect( 0, 0, Game.screenW(), Game.screenH() );
      if ( this.sky ) renderElementAt( back, this.sky );
      for ( const sheet of this.confetti_layers )
        renderElementAt( back, sheet );
    }

    if ( !this.shouldBeRendered() )
      return;

    if ( this.dom_mode )
    {
      const gradient_h = LayoutScaler.heightFromDesign( 2340 );
      this.setDomBackdrop( `linear-gradient(to top, #4E5BD4 0%, #650486 50.6%, #C66026 100%) center bottom / 100% ${gradient_h}px no-repeat` );
      this.startRender();
      this.finishRender();
      SafeAreas.syncBothSafeAreaOverlaysDom( this.getDomHost() );
      return;
    }

    const ctx = this.ctx;
    ctx.save();
    ctx.setTransform( 1, 0, 0, 1, 0, 0 );
    ctx.clearRect( 0, 0, ctx.canvas.width, ctx.canvas.height );
    ctx.restore();
    this.startRender();
    this.finishRender();
    SafeAreas.renderBothSafeAreaOverlays( ctx );
  }

  public name(): string
  {
    return "OnePlusTwoOfferScreen";
  }
}
